/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Pure helpers for the customer-portal applications in the v2 Real Estate app
 * (module RE_CUSTOMER_PORTAL, docs/ui-v2/RE_PORTAL.md 5c). The status rules
 * live on the backend (real_estate/portal/flow.py); the record page shows the
 * moves the backend lists in allowed_moves, so nothing here decides them.
 */

export const APPLICATION_STAGES = ["SUBMITTED", "UNDER_REVIEW", "APPROVED", "CONVERTED"];

export const STATUS_LABEL = {
  SUBMITTED: "New",
  UNDER_REVIEW: "In review",
  INFO_REQUESTED: "Waiting on applicant",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CONVERTED: "Converted",
  WITHDRAWN: "Withdrawn",
};

export const STATUS_TONE = {
  SUBMITTED: "info",
  UNDER_REVIEW: "info",
  INFO_REQUESTED: "warning",
  APPROVED: "success",
  REJECTED: "danger",
  CONVERTED: "success",
  WITHDRAWN: "neutral",
};

/** Queue filters: the open work first. */
export const QUEUE_FILTERS = [
  { value: "SUBMITTED,UNDER_REVIEW,INFO_REQUESTED", label: "Open", statuses: ["SUBMITTED", "UNDER_REVIEW", "INFO_REQUESTED"] },
  { value: "SUBMITTED", label: "New", statuses: ["SUBMITTED"] },
  { value: "INFO_REQUESTED", label: "Waiting on applicant", statuses: ["INFO_REQUESTED"] },
  { value: "APPROVED", label: "Approved", statuses: ["APPROVED"] },
  { value: "CONVERTED,REJECTED,WITHDRAWN", label: "Closed", statuses: ["CONVERTED", "REJECTED", "WITHDRAWN"] },
  { value: "", label: "All", statuses: null },
];

/** How many applications a filter covers, from the list's counts by status. */
export function filterCount(filter, counts) {
  const c = counts || {};
  const keys = filter.statuses || Object.keys(c);
  return keys.reduce((n, k) => n + (Number(c[k]) || 0), 0);
}

export const typeLabel = (type, au) => {
  if (type === "RENTAL") return "Rental application";
  return au ? "Offer" : "Purchase application";
};

/**
 * Where the application sits on the stage path. Info requested sits on
 * "In review"; rejected and withdrawn end the path early.
 */
export function stageFor(status) {
  if (status === "INFO_REQUESTED") return { activeStep: 1, finalState: null };
  if (status === "REJECTED" || status === "WITHDRAWN") return { activeStep: -1, finalState: STATUS_LABEL[status] };
  return { activeStep: Math.max(0, APPLICATION_STAGES.indexOf(status)), finalState: null };
}

/**
 * The buttons for the moves the backend allows. A reason is required to
 * reject and to ask for information (the backend refuses without one).
 */
export function actionsFor(allowedMoves, { canUpdate }) {
  if (!canUpdate) return [];
  const moves = new Set(allowedMoves || []);
  const out = [];
  if (moves.has("UNDER_REVIEW")) out.push({ to: "UNDER_REVIEW", label: "Start review", needsReason: false });
  if (moves.has("INFO_REQUESTED")) out.push({ to: "INFO_REQUESTED", label: "Ask for information", needsReason: true });
  if (moves.has("APPROVED")) out.push({ to: "APPROVED", label: "Approve", needsReason: false, primary: true });
  if (moves.has("REJECTED")) out.push({ to: "REJECTED", label: "Reject", needsReason: true, danger: true });
  return out;
}

const PER_YEAR = { WEEKLY: 52, FORTNIGHTLY: 26, MONTHLY: 12, QUARTERLY: 4, SEMI_ANNUAL: 2, YEARLY: 1, ANNUAL: 1, ANNUALLY: 1 };

/** A listing's rent as a yearly figure (the lease form takes annual rent). */
export function annualRent(price, frequency) {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return null;
  const times = PER_YEAR[String(frequency || "").toUpperCase()] || 1;
  return Math.round(n * times * 100) / 100;
}

/** The day before the same date `months` later (a 12-month lease from 1 Dec ends 30 Nov). */
export function leaseEnd(startIso, months) {
  const m = Number(months);
  if (!startIso || !Number.isInteger(m) || m <= 0) return null;
  const [y, mo, d] = String(startIso).slice(0, 10).split("-").map(Number);
  if (!y || !mo || !d) return null;
  const end = new Date(Date.UTC(y, mo - 1 + m, d));
  end.setUTCDate(end.getUTCDate() - 1);
  return end.toISOString().slice(0, 10);
}

/**
 * Where Convert goes: the classic Booking wizard or the Leases page's New
 * lease dialog, carrying the application so the form fills itself in and links
 * the saved record back. The agent still saves the record.
 */
export function conversionPath(app) {
  if (!app) return null;
  if (app.type === "PURCHASE") {
    const unit = app.unit_id ? `unit_id=${app.unit_id}&` : "";
    return `/real-estate/bookings/new?${unit}application_id=${app.id}`;
  }
  return `/real-estate/leases?application_id=${app.id}`;
}

/** Checklist items: a stable key from a new item's label (the backend's key pattern). */
export function checklistKey(label, taken = []) {
  const base = String(label || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([^a-z])/, "d_$1")
    .slice(0, 50) || "document";
  let key = base;
  let n = 2;
  while (taken.includes(key)) key = `${base}_${n++}`;
  return key;
}

/** The checklist as the backend takes it, or the first problem. */
export function checkChecklist(items) {
  const seen = new Set();
  for (const item of items || []) {
    const label = String(item.label || "").trim();
    if (!label) return { error: "Every document needs a name." };
    if (label.length > 200) return { error: "Keep document names under 200 characters." };
    if (!/^[a-z][a-z0-9_]{0,59}$/.test(item.key)) return { error: `"${label}" has an invalid key.` };
    if (seen.has(item.key)) return { error: `"${label}" is listed twice.` };
    seen.add(item.key);
  }
  if ((items || []).length > 20) return { error: "Ask for at most 20 documents." };
  return { items: (items || []).map((i) => ({ key: i.key, label: String(i.label).trim(), required: Boolean(i.required) })) };
}

/**
 * The New lease form's starting values from a rental application's
 * conversion pre-fill (GET /re/applications/{id}/conversion). Only a rent
 * frequency the form offers is used; the end date follows the lease term the
 * applicant asked for, else the form's own default.
 */
export function leaseFormFrom(prefill, allowedFrequencies = []) {
  const p = prefill || {};
  const patch = {};
  if (p.unit_id) patch.unit_id = p.unit_id;
  if (p.start_date) patch.start_date = String(p.start_date).slice(0, 10);
  const end = leaseEnd(patch.start_date, p.lease_term_months);
  if (end) patch.end_date = end;
  const yearly = annualRent(p.listed_rent, p.rent_frequency);
  if (yearly) patch.annual_rent = String(yearly);
  if (p.rent_frequency && allowedFrequencies.includes(p.rent_frequency)) patch.rent_frequency = p.rent_frequency;
  if (p.number_of_cheques) patch.number_of_cheques = p.number_of_cheques;
  return { patch, tenantContactId: p.contact_id || null };
}
